import { isAxiosError } from "axios";
import i18n, { t } from "i18next";
import axios from "../lib/axios";

type ApiResult<T = any> =
  | { isOk: true; message: string; data: T }
  | { isOk: false; error: string };

const extractError = (error: unknown): string => {
  let message = t("common.unknownErrorOccurred");
  if (isAxiosError(error)) {
    if (error.response) {
      if (error.response.status === 403) {
        message = t("noPermissions");
      } else if (error.response.data) {
        const data = error.response.data;
        if (
          "errors" in data &&
          Array.isArray(data.errors) &&
          data.errors.length > 0 &&
          typeof data.errors[0] === "object" &&
          "message" in data.errors[0]
        ) {
          message = data.errors[0].message;
        } else if ("message" in data) {
          message = data.message;
        }
      }
    } else if (error.request) {
      message = t("common.noResponseFromServer");
    }
  }
  return message;
};

const jsonHeaders = () => ({ "Accept-Language": i18n.language });

const toLocalIsoString = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export async function EmployeesToRental2(companyId?: number): Promise<ApiResult> {
  try {
    const response = await axios.get(`/api/employees-to-rental2`, {
      params: companyId ? { companyId } : undefined,
      headers: jsonHeaders(),
    });
    return {
      isOk: true,
      message: response.data?.message || t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    return { isOk: false, error: extractError(error) };
  }
}

export async function ShareEmployee2(
  companyId: number,
  employeeId: number,
  releaseStartDate: Date,
  releaseEndDate: Date,
  indefiniteTime: boolean,
): Promise<ApiResult> {
  try {
    const response = await axios.post(
      `/api/share-employee2`,
      {
        companyId,
        employeeId,
        releaseStartDate: toLocalIsoString(releaseStartDate),
        releaseEndDate: toLocalIsoString(releaseEndDate),
        indefiniteTime,
      },
      { headers: jsonHeaders() },
    );
    return {
      isOk: true,
      message: response.data?.message || t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    return { isOk: false, error: extractError(error) };
  }
}

export async function EditReleaseRange2(
  companyId: number,
  employeeId: number,
  releaseStartDate: Date,
  releaseEndDate: Date,
  indefiniteTime: boolean,
): Promise<ApiResult> {
  try {
    const response = await axios.put(
      `/api/rental-release-range2`,
      {
        companyId,
        employeeId,
        releaseStartDate: toLocalIsoString(releaseStartDate),
        releaseEndDate: toLocalIsoString(releaseEndDate),
        indefiniteTime,
      },
      { headers: jsonHeaders() },
    );
    return {
      isOk: true,
      message: response.data?.message || t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    return { isOk: false, error: extractError(error) };
  }
}

export async function EmployeesAvailableForAssignment2(companyId?: number): Promise<ApiResult> {
  try {
    const response = await axios.get(`/api/employees-available-for-assignment2`, {
      params: companyId ? { companyId } : undefined,
      headers: jsonHeaders(),
    });
    return {
      isOk: true,
      message: response.data?.message || t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    return { isOk: false, error: extractError(error) };
  }
}

export async function AcceptEmployee2(
  companyId: number,
  employeeId: number,
  startDateOfTheRental: Date,
  endDateOfTheRental: Date,
): Promise<ApiResult> {
  try {
    const response = await axios.post(
      `/api/accept-employee2`,
      {
        companyId,
        employeeId,
        startDateOfTheRental: toLocalIsoString(startDateOfTheRental),
        endDateOfTheRental: toLocalIsoString(endDateOfTheRental),
      },
      { headers: jsonHeaders() },
    );
    return {
      isOk: true,
      message: response.data?.message || t("Employee accepted."),
      data: response.data,
    };
  } catch (error) {
    return { isOk: false, error: extractError(error) };
  }
}

export async function DeleteEmployeeFromList2(
  companyId: number,
  employeeId: number,
): Promise<ApiResult> {
  try {
    const response = await axios.delete(`/api/remove-employee2`, {
      params: { companyId, employeeId },
      headers: jsonHeaders(),
    });
    return {
      isOk: true,
      message: response.data?.message || t("Employee deleted."),
      data: response.data,
    };
  } catch (error) {
    return { isOk: false, error: extractError(error) };
  }
}

export async function WithdrawEmployee2(
  companyId: number,
  employeeId: number,
): Promise<ApiResult> {
  try {
    const response = await axios.post(`/api/withdraw-employee2`, null, {
      params: { companyId, employeeId },
      headers: jsonHeaders(),
    });
    return {
      isOk: true,
      message: response.data?.message || t("transferOfEmployees.employeeLoanWithdrawn"),
      data: response.data,
    };
  } catch (error) {
    return { isOk: false, error: extractError(error) };
  }
}

export async function RentalHistoryEmployees2(companyId?: number): Promise<ApiResult> {
  try {
    const response = await axios.get(`/api/rental-history-employees2`, {
      params: companyId ? { companyId } : undefined,
      headers: jsonHeaders(),
    });
    return {
      isOk: true,
      message: response.data?.message || t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    return { isOk: false, error: extractError(error) };
  }
}

export async function RentalHistoriesByEmployee2(
  companyId: number,
  employeeId: number,
): Promise<ApiResult> {
  try {
    const response = await axios.get(`/api/rental-histories2`, {
      params: { companyId, employeeId },
      headers: jsonHeaders(),
    });
    return {
      isOk: true,
      message: response.data?.message || t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    return { isOk: false, error: extractError(error) };
  }
}
